import { browser } from '$app/environment';
import { getDayKey, getDayNumber } from './daily';

export type GameKey = 'word-guess' | 'word-build' | 'word-group' | 'word-cross' | 'word-cross-m' | 'word-find' | 'word-crypto' | 'word-speed' | 'num-fill' | 'word-tower';

export type WordGuessProgress = {
	guessesUsed: number;
	solved: boolean;
	complete: boolean;
	coop?: boolean;
	coopPartner?: string;
};

export type WordBuildProgress = {
	foundWords: number;
	totalWords: number;
	score: number;
	maxScore: number;
	currentRank: string;
	nextRank: string | null;
	nextGoal: number | null;
	coop?: boolean;
	coopPartner?: string;
};

export type WordGroupProgress = {
	solvedGroups: number;
	totalGroups: number;
	mistakesRemaining: number;
	complete: boolean;
	coop?: boolean;
	coopPartner?: string;
};

export type WordCrossProgress = {
	filledCells: number;
	totalCells: number;
	revealed: boolean;
	checksUsed?: number;
	complete: boolean;
	coop?: boolean;
	coopPartner?: string;
};

export type WordFindProgress = {
	foundWords: number;
	totalWords: number;
	foundThemeWords: number;
	totalThemeWords: number;
	foundSpangram: boolean;
	hintsUsed?: number;
	complete: boolean;
	coop?: boolean;
	coopPartner?: string;
};

export type WordCryptoProgress = {
	mistakes: number;
	hintsUsed: number;
	lettersSolved: number;
	totalLetters: number;
	complete: boolean;
	solved: boolean;
	coop?: boolean;
	coopPartner?: string;
};

export type WordSpeedProgress = {
	score: number;
	words: number;
	wpm: number;
	accuracy: number;
	complete: boolean;
	coop?: boolean;
	coopPartner?: string;
};

export type NumFillProgress = {
	difficulty: 'easy' | 'medium' | 'hard';
	filledCells: number;
	totalCells: number;
	mistakes: number;
	hintsUsed: number;
	complete: boolean;
	solved: boolean;
	coop?: boolean;
	coopPartner?: string;
};

export type WordTowerProgress = {
	score: number;
	words: number;
	longestWord: string;
	complete: boolean;
	coop?: boolean;
	coopPartner?: string;
};

export type WordGuessState = {
	board: Array<Array<{ letter: string; state?: 'absent' | 'present' | 'correct'; locked: boolean }>>;
	currentRow: number;
	currentCol: number;
	gameOver: boolean;
	keyStates: Record<string, 'absent' | 'present' | 'correct' | undefined>;
	hintCount?: number;
};

export type WordBuildState = {
	letters: string[];
	currentWord: string;
	foundWords: string[];
	hintCount: number;
};

export type WordGroupState = {
	board: Array<{ label: string; category: string }>;
	selected: string[];
	solved: string[];
	mistakesRemaining: number;
};

export type WordCrossState = {
	entries: string[][];
	activeCell: [number, number];
	activeDirection: 'across' | 'down';
	revealed: boolean;
	incorrectKeys: string[];
};

export type WordFindState = {
	foundWords: string[];
	hintsUsed: number;
	bankedWords: string[];
	hintedWord: string | null;
	hintLevel: number;
	currentPath: Array<[number, number]>;
	tracePreview: string;
};

export type WordCryptoState = {
	guesses: Record<number, string>;
	revealed: number[];
	mistakes: number;
	hintCount: number;
	gameOver: boolean;
};

export type WordSpeedState = {
	score: number;
	words: number;
	wpm: number;
	accuracy: number;
	finished: boolean;
};

export type NumFillState = {
	difficulty: 'easy' | 'medium' | 'hard';
	/** 81-length, row-major. Player + given values; 0 = empty. */
	values: number[];
	/** 81-length list of candidate-mark arrays (pencil marks per cell). */
	candidates: number[][];
	mistakes: number;
	hintCount: number;
	gameOver: boolean;
};

export type WordTowerState = {
	/** Row-major grid letters; empty cells are ''. Tile ids are not persisted. */
	grid: string[][];
	score: number;
	riseIndex: number;
	gameOver: boolean;
};

export type ProgressSnapshot = {
	'word-guess'?: WordGuessProgress;
	'word-build'?: WordBuildProgress;
	'word-group'?: WordGroupProgress;
	'word-cross'?: WordCrossProgress;
	'word-cross-m'?: WordCrossProgress;
	'word-find'?: WordFindProgress;
	'word-crypto'?: WordCryptoProgress;
	'word-speed'?: WordSpeedProgress;
	'num-fill'?: NumFillProgress;
	'word-tower'?: WordTowerProgress;
};

export type GameStateSnapshot = {
	'word-guess'?: WordGuessState;
	'word-build'?: WordBuildState;
	'word-group'?: WordGroupState;
	'word-cross'?: WordCrossState;
	'word-cross-m'?: WordCrossState;
	'word-find'?: WordFindState;
	'word-crypto'?: WordCryptoState;
	'word-speed'?: WordSpeedState;
	'num-fill'?: NumFillState;
	'word-tower'?: WordTowerState;
};

export type GameStreak = {
	current: number;
	best: number;
	completedToday: boolean;
};

type ProgressStore = Record<string, ProgressSnapshot>;
type GameStateStore = Record<string, GameStateSnapshot>;
type CompletionStore = Record<GameKey, string[]>;

const STORAGE_KEY = 'bubble-games-progress';
const STATE_STORAGE_KEY = 'bubble-games-state';
const COMPLETION_STORAGE_KEY = 'bubble-games-completions';
const MAX_PROGRESS_DAYS = 400;
const MAX_STATE_DAYS = 7;
const MAX_COMPLETION_DAYS = 400;

function readProgressStore() {
	if (!browser) return {} satisfies ProgressStore;

	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return {} satisfies ProgressStore;
		const parsed = JSON.parse(raw);
		return typeof parsed === 'object' && parsed ? (parsed as ProgressStore) : ({} satisfies ProgressStore);
	} catch {
		return {} satisfies ProgressStore;
	}
}

function writeProgressStore(store: ProgressStore) {
	if (!browser) return;

	const dayKeys = Object.keys(store).sort();
	const trimmedStore = dayKeys.slice(-MAX_PROGRESS_DAYS).reduce<ProgressStore>((nextStore, dayKey) => {
		nextStore[dayKey] = store[dayKey];
		return nextStore;
	}, {});

	window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedStore));
	return trimmedStore;
}

function readGameStateStore() {
	if (!browser) return {} satisfies GameStateStore;

	try {
		const raw = window.localStorage.getItem(STATE_STORAGE_KEY);
		if (!raw) return {} satisfies GameStateStore;
		const parsed = JSON.parse(raw);
		return typeof parsed === 'object' && parsed ? (parsed as GameStateStore) : ({} satisfies GameStateStore);
	} catch {
		return {} satisfies GameStateStore;
	}
}

function writeGameStateStore(store: GameStateStore) {
	if (!browser) return;

	const dayKeys = Object.keys(store).sort();
	const trimmedStore = dayKeys.slice(-MAX_STATE_DAYS).reduce<GameStateStore>((nextStore, dayKey) => {
		nextStore[dayKey] = store[dayKey];
		return nextStore;
	}, {});

	window.localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(trimmedStore));
	return trimmedStore;
}

function createEmptyCompletionStore(): CompletionStore {
	return {
		'word-guess': [],
		'word-build': [],
		'word-group': [],
		'word-cross': [],
		'word-cross-m': [],
		'word-find': [],
		'word-crypto': [],
		'word-speed': [],
		'num-fill': [],
		'word-tower': []
	};
}

function readCompletionStore() {
	if (!browser) return createEmptyCompletionStore();

	try {
		const raw = window.localStorage.getItem(COMPLETION_STORAGE_KEY);
		if (!raw) return createEmptyCompletionStore();
		const parsed = JSON.parse(raw);
		if (typeof parsed !== 'object' || !parsed) return createEmptyCompletionStore();

		return {
			'word-guess': Array.isArray(parsed['word-guess']) ? parsed['word-guess'] : [],
			'word-build': Array.isArray(parsed['word-build']) ? parsed['word-build'] : [],
			'word-group': Array.isArray(parsed['word-group']) ? parsed['word-group'] : [],
			'word-cross': Array.isArray(parsed['word-cross']) ? parsed['word-cross'] : [],
			'word-cross-m': Array.isArray(parsed['word-cross-m']) ? parsed['word-cross-m'] : [],
			'word-find': Array.isArray(parsed['word-find']) ? parsed['word-find'] : [],
			'word-crypto': Array.isArray(parsed['word-crypto']) ? parsed['word-crypto'] : [],
			'word-speed': Array.isArray(parsed['word-speed']) ? parsed['word-speed'] : [],
			'num-fill': Array.isArray(parsed['num-fill']) ? parsed['num-fill'] : [],
			'word-tower': Array.isArray(parsed['word-tower']) ? parsed['word-tower'] : []
		} satisfies CompletionStore;
	} catch {
		return createEmptyCompletionStore();
	}
}

function writeCompletionStore(store: CompletionStore) {
	if (!browser) return;

	const trimmedStore = Object.fromEntries(Object.entries(store).map(([game, dayKeys]) => [game, [...new Set(dayKeys)].sort().slice(-MAX_COMPLETION_DAYS)])) as CompletionStore;

	window.localStorage.setItem(COMPLETION_STORAGE_KEY, JSON.stringify(trimmedStore));
	return trimmedStore;
}

export function readProgressHistory() {
	return readProgressStore();
}

export function readProgressSnapshot(date = new Date()) {
	const store = readProgressStore();
	return store[getDayKey(date)] ?? ({} satisfies ProgressSnapshot);
}

export function readGameStateSnapshot(date = new Date()) {
	const store = readGameStateStore();
	return store[getDayKey(date)] ?? ({} satisfies GameStateSnapshot);
}

export function readGameState<TKey extends GameKey>(game: TKey, date = new Date()) {
	const snapshot = readGameStateSnapshot(date);
	return snapshot[game] as GameStateSnapshot[TKey] | undefined;
}

export function clearGameProgress(game: GameKey, date = new Date()) {
	if (!browser) return;

	const dayKey = getDayKey(date);
	const store = readProgressStore();
	const snapshot = store[dayKey];

	if (!snapshot?.[game]) return;

	delete snapshot[game];

	if (Object.keys(snapshot).length === 0) {
		delete store[dayKey];
	} else {
		store[dayKey] = snapshot;
	}

	writeProgressStore(store);
}

export function clearGameState(game: GameKey, date = new Date()) {
	if (!browser) return;

	const dayKey = getDayKey(date);
	const store = readGameStateStore();
	const snapshot = store[dayKey];

	if (!snapshot?.[game]) return;

	delete snapshot[game];

	if (Object.keys(snapshot).length === 0) {
		delete store[dayKey];
	} else {
		store[dayKey] = snapshot;
	}

	writeGameStateStore(store);
}

export function writeGameProgress<TKey extends GameKey>(game: TKey, value: ProgressSnapshot[TKey], date = new Date()) {
	if (!browser) return;

	const dayKey = getDayKey(date);
	const store = readProgressStore();
	store[dayKey] = {
		...(store[dayKey] ?? {}),
		[game]: value
	};

	writeProgressStore(store);
}

export function writeGameState<TKey extends GameKey>(game: TKey, value: GameStateSnapshot[TKey], date = new Date()) {
	if (!browser) return;

	const dayKey = getDayKey(date);
	const store = readGameStateStore();
	store[dayKey] = {
		...(store[dayKey] ?? {}),
		[game]: value
	};

	writeGameStateStore(store);
}

export function markGameCompleted(game: GameKey, date = new Date()) {
	if (!browser) return;

	const dayKey = getDayKey(date);
	const store = readCompletionStore();
	store[game] = [...store[game], dayKey];
	writeCompletionStore(store);
}

function calculateStreak(dayKeys: string[], today = new Date()) {
	const sortedUniqueDayKeys = [...new Set(dayKeys)].sort();
	const completedToday = sortedUniqueDayKeys.includes(getDayKey(today));

	let current = 0;
	let best = 0;
	let streak = 0;
	let previousDayNumber: number | null = null;

	for (let index = sortedUniqueDayKeys.length - 1; index >= 0; index -= 1) {
		const dayNumber = getDayNumber(new Date(`${sortedUniqueDayKeys[index]}T00:00:00`));

		if (previousDayNumber === null || previousDayNumber - dayNumber === 1) {
			streak += 1;
		} else if (previousDayNumber !== dayNumber) {
			streak = 1;
		}

		best = Math.max(best, streak);
		previousDayNumber = dayNumber;
	}

	if (sortedUniqueDayKeys.length > 0) {
		const todayDayNumber = getDayNumber(today);
		const latestDayNumber = getDayNumber(new Date(`${sortedUniqueDayKeys.at(-1)}T00:00:00`));

		if (latestDayNumber === todayDayNumber) {
			current = 1;
			for (let index = sortedUniqueDayKeys.length - 2; index >= 0; index -= 1) {
				const dayNumber = getDayNumber(new Date(`${sortedUniqueDayKeys[index]}T00:00:00`));
				if (todayDayNumber - dayNumber === current) {
					current += 1;
				} else {
					break;
				}
			}
		}
	}

	return {
		current,
		best,
		completedToday
	} satisfies GameStreak;
}

export function readGameStreak(game: GameKey, date = new Date()) {
	const store = readCompletionStore();
	return calculateStreak(store[game], date);
}

export function readAllGameStreaks(date = new Date()) {
	const store = readCompletionStore();

	return {
		'word-guess': calculateStreak(store['word-guess'], date),
		'word-build': calculateStreak(store['word-build'], date),
		'word-group': calculateStreak(store['word-group'], date),
		'word-cross': calculateStreak(store['word-cross'], date),
		'word-cross-m': calculateStreak(store['word-cross-m'], date),
		'word-find': calculateStreak(store['word-find'], date),
		'word-crypto': calculateStreak(store['word-crypto'], date),
		'word-speed': calculateStreak(store['word-speed'], date),
		'num-fill': calculateStreak(store['num-fill'], date),
		'word-tower': calculateStreak(store['word-tower'], date)
	} satisfies Record<GameKey, GameStreak>;
}

export function readCompletedTodayCount(date = new Date()) {
	const streaks = readAllGameStreaks(date);
	return Object.values(streaks).filter((streak) => streak.completedToday).length;
}

export type ArchiveDay = {
	dayKey: string;
	games: Record<GameKey, boolean>;
};

export function readArchive(): ArchiveDay[] {
	const store = readCompletionStore();

	const allDayKeys = new Set<string>();
	for (const dayKeys of Object.values(store)) {
		for (const key of dayKeys) allDayKeys.add(key);
	}

	return [...allDayKeys]
		.sort()
		.reverse()
		.map((dayKey) => ({
			dayKey,
			games: {
				'word-guess': store['word-guess'].includes(dayKey),
				'word-build': store['word-build'].includes(dayKey),
				'word-group': store['word-group'].includes(dayKey),
				'word-cross': store['word-cross'].includes(dayKey),
				'word-cross-m': store['word-cross-m'].includes(dayKey),
				'word-find': store['word-find'].includes(dayKey),
				'word-crypto': store['word-crypto'].includes(dayKey),
				'word-speed': store['word-speed'].includes(dayKey),
				'num-fill': store['num-fill'].includes(dayKey),
				'word-tower': store['word-tower'].includes(dayKey)
			} satisfies Record<GameKey, boolean>
		}));
}
